import {
  isDimensionalFramingProfileDescriptor,
  isIJoistFramingProfileDescriptor,
  isRectangularFramingProfileDescriptor,
} from "building-types/framing/floorFramingProfileDescriptorTools";
import type {
  CompositeLumberComponent,
  CustomFramingComponent,
  DimensionalLumberComponent,
  FramingComponentEntity,
  IJoistComponent,
  TrussComponent,
} from "building-types/framing/framingComponentTypes";
import { FramingComponentType } from "building-types/framing/framingComponentTypes";
import type { FramingProfileDescriptor } from "building-types/framing/framingProfileDescriptorTypes";
import { FramingProfileSectionKind } from "building-types/framing/framingProfileDescriptorTypes";
import type { IJoistProfileDescriptor } from "building-types/framing/iJoistTypes";
import { IJoistProfileCatalog } from "building-types/framing/iJoistTypes";
import type {
  LumberProfileDescriptor,
  LumberLengthDescriptor,
  LumberRailsByType,
  LumberPiecesByProfile,
  LumberPiecesByProfileSet,
  LumberRail,
  StandardLumberCounter,
  WastedLumber,
  StandardLumberCounterSet,
  WastedLumberSet,
  Plywood,
  OSBProfile,
  OSB,
} from "building-types/framing/lumberTypes";
import {
  LumberLengthType,
  LUMBER_UNION_GAP,
  LumberProfileType,
  PlywoodProfile,
  SpanRating,
  SubstrateProfileType,
  LumberProfileCatalog,
} from "building-types/framing/lumberTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { GeneratedEntityHandle } from "building-types/handles/generatedEntityHandleTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import uid from "common/utils/uid";
import { DIST_EPSILON, ZERO_EPSILON } from "geom/core";
import type { Plane } from "geom/planeTypes";
import { type PolyLine2, polyLine2New } from "geom/polyLine2Types";
import { type PolyLine3, polyLine3Length } from "geom/polyLine3";
import { ascendingSortCompare } from "geom/utils";

function lumberLengthDescriptorNew(type: LumberLengthType, name: string, length: number): LumberLengthDescriptor {
  return {
    type,
    name,
    length,
  };
}

export function lumberPiecesByProfileNew(
  profileDescriptor: LumberProfileDescriptor,
  railsByType: LumberRailsByType = {},
  generatingHandles: Set<DocEntityHandle | GeneratedEntityHandle> = new Set(),
): LumberPiecesByProfile {
  return {
    id: uid(),
    generatingHandles,
    profileDescriptor,
    railsByType,
  };
}

export function lumberPiecesByProfileSetNew(
  profileDescriptors: LumberProfileDescriptor[] = [],
  generatingHandles: Set<DocEntityHandle | GeneratedEntityHandle> = new Set(),
): LumberPiecesByProfileSet {
  return profileDescriptors.reduce((set, descriptor) => {
    set[descriptor.type] = lumberPiecesByProfileNew(descriptor, undefined, generatingHandles);
    return set;
  }, {});
}

export function lumberRailNew(
  rail: PolyLine3,
  nX: number,
  nY: number,
  nZ: number,
  clipPlanes?: Plane[],
  startExtension?: number,
  endExtension?: number,
  id?: string,
): LumberRail {
  const railId = id ?? uid();
  return {
    id: railId,
    handle: {
      entityType: "LUMBER",
      id: railId,
    },
    rail,
    nX,
    nY,
    nZ,
    length: polyLine3Length(rail),
    clipPlanes,
    startExtension,
    endExtension,
  };
}
function standardLumberCounterNew(
  profileDescriptor: LumberProfileDescriptor,
  lengthDescriptor: LumberLengthDescriptor,
  count: number = 0,
  wasted: number[] = [],
): StandardLumberCounter {
  return {
    profileDescriptor,
    lengthDescriptor,
    count,
    wasted,
  };
}
function wastedLumberNew(
  profileDescriptor: LumberProfileDescriptor,
  totalLength: number = 0,
  wastedLength: number = 0,
  wastedVolume: number = 0,
  percentage: number = 0,
): WastedLumber {
  return {
    profileDescriptor,
    totalLength,
    wastedLength,
    wastedVolume,
    percentage,
  };
}

const LumberLengthCatalog: {
  [key in LumberLengthType]: LumberLengthDescriptor;
} = {
  // L6: lumberLengthDescriptorNew(LumberLengthType.L6, "6", 72),
  L8: lumberLengthDescriptorNew(LumberLengthType.L8, "8", 96),
  L10: lumberLengthDescriptorNew(LumberLengthType.L10, "10", 120),
  L12: lumberLengthDescriptorNew(LumberLengthType.L12, "12", 144),
  L14: lumberLengthDescriptorNew(LumberLengthType.L14, "14", 168),
  L16: lumberLengthDescriptorNew(LumberLengthType.L16, "16", 192),
  L18: lumberLengthDescriptorNew(LumberLengthType.L18, "18", 216),
  L20: lumberLengthDescriptorNew(LumberLengthType.L20, "20", 240),
  L22: lumberLengthDescriptorNew(LumberLengthType.L22, "22", 264),
  L24: lumberLengthDescriptorNew(LumberLengthType.L24, "24", 288),
};
// add them in order (from shorter to longer)

export const StudLumberLengths = [LumberLengthCatalog.L8, LumberLengthCatalog.L10];
export const PlateLumberLengths = [LumberLengthCatalog.L14];
export const HeaderLumberLengths = [
  LumberLengthCatalog.L8,
  LumberLengthCatalog.L10,
  LumberLengthCatalog.L12,
  LumberLengthCatalog.L14,
];
export const JoistLumberLengths = [
  LumberLengthCatalog.L8,
  LumberLengthCatalog.L10,
  LumberLengthCatalog.L12,
  LumberLengthCatalog.L14,
  LumberLengthCatalog.L16,
  LumberLengthCatalog.L18,
  LumberLengthCatalog.L20,
  LumberLengthCatalog.L22,
  LumberLengthCatalog.L24,
];
function getLumberFromWasted(
  lumberCounters: StandardLumberCounterSet,
  profileType: LumberProfileType,
  length: number,
): boolean {
  for (const lumberType in lumberCounters) {
    const studLumberCounter = lumberCounters[lumberType];
    const { wasted, lengthDescriptor, profileDescriptor } = studLumberCounter;

    if (profileDescriptor.type !== profileType || length >= lengthDescriptor.length) {
      continue;
    }

    for (let i = 0, l = wasted.length; i < l; i++) {
      const wastedLength = wasted[i];
      if (wastedLength >= length) {
        wasted[i] -= length;
        wasted.sort(ascendingSortCompare);
        return true;
      }
    }
  }

  return false;
}
function addLumberToWasted(lumberCounter: StandardLumberCounter, wastedLength: number) {
  if (wastedLength > DIST_EPSILON) {
    const { wasted } = lumberCounter;
    wasted.push(wastedLength);
    wasted.sort(ascendingSortCompare);
  }
}

export function getLumberPiece(
  lumberCounters: StandardLumberCounterSet,
  profileDescriptor: LumberProfileDescriptor,
  pieceType: string,
  forceSingleLumberPiece: boolean,
  length: number,
  getBestLumberLength: (pieceType: string, length: number) => LumberLengthDescriptor,
) {
  // look in wasted
  if (getLumberFromWasted(lumberCounters, profileDescriptor.type, length)) {
    return;
  }

  // get a new piece of lumber
  const lengthDescriptor = getBestLumberLength(pieceType, length);
  const { length: standardLength, type: standardType } = lengthDescriptor;
  const lumberType = profileDescriptor.type + standardType;

  // create new counter if needed
  if (!lumberCounters[lumberType]) {
    lumberCounters[lumberType] = standardLumberCounterNew(profileDescriptor, lengthDescriptor);
  }

  const currentCounter = lumberCounters[lumberType];

  if (forceSingleLumberPiece) {
    // console.assert(standardLength >= length, "Lumber length is bigger than any defined lumber length for this type.");
    // add new piece
    currentCounter.count++;
  } else {
    // add entire pieces
    while (length >= standardLength) {
      length -= standardLength;
      currentCounter.count++;
    }

    if (length > DIST_EPSILON) {
      // look in wasted again
      if (!getLumberFromWasted(lumberCounters, profileDescriptor.type, length)) {
        currentCounter.count++;
      }
    }
  }

  // add to wasted the remaining part
  addLumberToWasted(currentCounter, standardLength - length);
}

export function getWastedLumber(lumberCounters: StandardLumberCounterSet): WastedLumberSet {
  const wastedLumber: WastedLumberSet = {};

  for (const lumberType in lumberCounters) {
    const studLumberCounter = lumberCounters[lumberType];
    const { wasted, profileDescriptor, lengthDescriptor, count } = studLumberCounter;
    const { type: profileType } = profileDescriptor;

    // create new element if needed
    if (!wastedLumber[profileType]) {
      wastedLumber[profileType] = wastedLumberNew(profileDescriptor);
    }

    const wastedByProfile = wastedLumber[profileType];

    wastedByProfile.totalLength += lengthDescriptor.length * count;

    for (let i = 0, l = wasted.length; i < l; i++) {
      wastedByProfile.wastedLength += wasted[i];
    }
  }

  // update percentage and volume
  for (const profileType in wastedLumber) {
    const wastedByProfile = wastedLumber[profileType];
    const { profileDescriptor, totalLength, wastedLength } = wastedByProfile;
    const { width, height } = profileDescriptor;

    const profileArea = width * height;
    wastedByProfile.wastedVolume = profileArea * wastedLength;
    wastedByProfile.percentage = totalLength > DIST_EPSILON ? (wastedLength / totalLength) * 100 : 0;
  }

  return wastedLumber;
}

export function makeLumberProfile(profileDescriptor: LumberProfileDescriptor): PolyLine2 {
  const { width, height } = profileDescriptor;

  return polyLine2New([
    LUMBER_UNION_GAP,
    LUMBER_UNION_GAP,
    LUMBER_UNION_GAP,
    height,
    width - LUMBER_UNION_GAP,
    height,
    width - LUMBER_UNION_GAP,
    LUMBER_UNION_GAP,
    LUMBER_UNION_GAP,
    LUMBER_UNION_GAP,
  ]);
}

/**
 * Build a single closed polyline tracing the I-joist cross-section. The result is one
 * watertight outline (one flange + web + the other flange) — NOT three separate parts —
 * which is what callers extrude downstream as one solid beam.
 *
 * The profile's coordinate axes match `makeLumberProfile`'s convention so that the
 * I-joist orients identically to dimensional lumber when extruded along a rail:
 *   - profile x ∈ [0, depth]       (the larger dimension — vertical when extruded as a floor joist)
 *   - profile y ∈ [0, flangeWidth] (the smaller dimension — horizontal across the joist)
 * The two flanges sit at the extremes of the profile-x axis and the web bridges them
 * along profile-y. The same `LUMBER_UNION_GAP` inset applied by `makeLumberProfile`
 * on outer edges is applied here on the outer flange edges (not the inner web step)
 * so that adjacent members union cleanly when stacked.
 */
export function makeIJoistProfile(descriptor: IJoistProfileDescriptor): PolyLine2 {
  const W = descriptor.flangeWidth;
  const H = descriptor.depth;
  const tF = descriptor.flangeThickness;
  const tW = descriptor.webThickness;

  const webHigh = (W + tW) / 2;
  const webLow = (W - tW) / 2;

  return polyLine2New([
    LUMBER_UNION_GAP,
    LUMBER_UNION_GAP,
    LUMBER_UNION_GAP,
    W - LUMBER_UNION_GAP,
    tF,
    W - LUMBER_UNION_GAP,
    tF,
    webHigh,
    H - tF,
    webHigh,
    H - tF,
    W - LUMBER_UNION_GAP,
    H - LUMBER_UNION_GAP,
    W - LUMBER_UNION_GAP,
    H - LUMBER_UNION_GAP,
    LUMBER_UNION_GAP,
    H - tF,
    LUMBER_UNION_GAP,
    H - tF,
    webLow,
    tF,
    webLow,
    tF,
    LUMBER_UNION_GAP,
    LUMBER_UNION_GAP,
    LUMBER_UNION_GAP,
  ]);
}

/**
 * Always returns ONE closed polyline regardless of section kind so the resulting solid
 * is a single watertight extrusion.
 */
export function makeFramingProfile2D(descriptor: FramingProfileDescriptor): PolyLine2 {
  if (isIJoistFramingProfileDescriptor(descriptor)) {
    return makeIJoistProfile(descriptor.componentDescriptor);
  }
  return makeLumberProfile(rectangularLumberDescriptor(descriptor));
}

/**
 * Build a `LumberProfileDescriptor`-shaped object from a `FramingProfileDescriptor` for
 * call sites that need rectangular `width`/`height` (top-down rendering, sheathing
 * coverage, takeoffs). For I-joists this returns the bounding rectangle of the section
 * with the dimensional-lumber convention `width = depth, height = flangeWidth`.
 */
export function rectangularLumberDescriptor(descriptor: FramingProfileDescriptor): LumberProfileDescriptor {
  if (isDimensionalFramingProfileDescriptor(descriptor)) {
    return descriptor.componentDescriptor;
  }
  if (isIJoistFramingProfileDescriptor(descriptor) || isRectangularFramingProfileDescriptor(descriptor)) {
    return {
      type: LumberProfileType.CUSTOM,
      name: descriptor.name,
      width: descriptor.width,
      height: descriptor.height,
      annotationLabel: descriptor.annotationLabel,
    };
  }
  assertUnreachable(descriptor);
}

function lumberDescriptorFromComponent(component: DimensionalLumberComponent): LumberProfileDescriptor {
  return LumberProfileCatalog[component.lumberProfileType];
}

export function getFramingProfileDescriptor(component: FramingComponentEntity): FramingProfileDescriptor {
  const componentId: ForeignEntityId<"FRAMING_COMPONENT"> = component.id;
  switch (component.type) {
    case FramingComponentType.DIMENSIONAL_LUMBER: {
      const c = component as DimensionalLumberComponent;
      const lumber = lumberDescriptorFromComponent(c);
      return {
        sectionKind: FramingProfileSectionKind.DIMENSIONAL,
        componentId,
        componentType: component.type,
        name: c.name,
        annotationLabel: lumber.annotationLabel,
        width: lumber.width,
        height: lumber.height,
        componentDescriptor: lumber,
      };
    }
    case FramingComponentType.COMPOSITE_LUMBER: {
      const c = component as CompositeLumberComponent;
      return {
        sectionKind: FramingProfileSectionKind.COMPOSITE,
        componentId,
        componentType: component.type,
        name: c.name,
        annotationLabel: c.name,
        width: c.depth,
        height: c.width,
      };
    }
    case FramingComponentType.TRUSS: {
      const c = component as TrussComponent;
      return {
        sectionKind: FramingProfileSectionKind.TRUSS,
        componentId,
        componentType: component.type,
        name: c.name,
        annotationLabel: c.name,
        width: c.depth,
        height: c.width,
      };
    }
    case FramingComponentType.I_JOIST: {
      const c = component as IJoistComponent;
      const profile = IJoistProfileCatalog[c.iJoistProfileType];
      return {
        sectionKind: FramingProfileSectionKind.I_JOIST,
        componentId,
        componentType: component.type,
        name: c.name,
        annotationLabel: profile.annotationLabel,
        // Match the dimensional-lumber convention used by the rest of the framing
        // pipeline (`structuralThickness = profileDescriptor.width`,
        // subfloor expansion uses `defaultRimProfile.height`, etc.):
        //   width  = larger / depth  dimension (vertical when extruded as a floor joist)
        //   height = smaller / breadth dimension (horizontal across the joist)
        width: profile.depth,
        height: profile.flangeWidth,
        componentDescriptor: profile,
      };
    }
    case FramingComponentType.CUSTOM: {
      const c = component as CustomFramingComponent;
      return {
        sectionKind: FramingProfileSectionKind.CUSTOM,
        componentId,
        componentType: component.type,
        name: c.name,
        annotationLabel: c.name,
        width: c.depth,
        height: c.width,
      };
    }
  }
}

export function getFramingProfileDescriptorById(
  framingComponents: { readonly [id: string]: FramingComponentEntity },
  componentId: ForeignEntityId<"FRAMING_COMPONENT"> | string,
): FramingProfileDescriptor | undefined {
  const component = framingComponents[componentId];
  if (!component) {
    return undefined;
  }
  return getFramingProfileDescriptor(component);
}

export function getLumberProfile(thickness: number, _isPlate: boolean): LumberProfileDescriptor {
  if (thickness >= 11.25) {
    return LumberProfileCatalog.SPF_2X12;
  } else if (thickness >= 9.25) {
    return LumberProfileCatalog.SPF_2X10;
  } else if (thickness >= 7.25) {
    return LumberProfileCatalog.SPF_2X8;
  } else if (thickness >= 5.5) {
    return LumberProfileCatalog.SPF_2X6;
  } else {
    return LumberProfileCatalog.SPF_2X4;
  }
}

export function plywoodProfileNew(
  plywoodProfile: PlywoodProfile,
  name: string,
  spanRating: SpanRating,
  thickness: number,
): Plywood {
  return {
    substrateProfileType: SubstrateProfileType.PLYWOOD,
    name,
    spanRating,
    thickness,
    plywoodProfile,
  };
}

export function osbProfileNew(osbProfile: OSBProfile, name: string, spanRating: SpanRating, thickness: number): OSB {
  return {
    substrateProfileType: SubstrateProfileType.OSB,
    name,
    spanRating,
    thickness,
    osbProfile,
  };
}
/*profile list to be expanded*/
function defaultSubstrateProfileCatalogNew(sr: SpanRating, spt: SubstrateProfileType) {
  if (spt === SubstrateProfileType.PLYWOOD) {
    const substrateCatalog: {
      [key in PlywoodProfile]?: Plywood;
    } = {
      "5/16-u": plywoodProfileNew(PlywoodProfile["5/16-u"], "5/16-u", sr, 5 / 16),
      "3/8-u": plywoodProfileNew(PlywoodProfile["3/8-u"], "3/8-u", sr, 3 / 8),
      "15/32-u": plywoodProfileNew(PlywoodProfile["15/32-u"], "15/32-u", sr, 15 / 32),
      "3/4-u": plywoodProfileNew(PlywoodProfile["3/4-u"], "3/4-u", sr, 3 / 4),
      "1/2-u": plywoodProfileNew(PlywoodProfile["1/2-u"], "1/2-u", sr, 1 / 2),
      "19/32-u": plywoodProfileNew(PlywoodProfile["19/32-u"], "19/32-u", sr, 19 / 32),
      "5/8-u": plywoodProfileNew(PlywoodProfile["5/8-u"], "5/8-u", sr, 5 / 8),
      "19/32-s": plywoodProfileNew(PlywoodProfile["19/32-s"], "19/32-s", sr, 19 / 32),
      "5/8-s": plywoodProfileNew(PlywoodProfile["5/8-s"], "5/8-s", sr, 5 / 8),
      "23/32-s": plywoodProfileNew(PlywoodProfile["23/32-s"], "23/32-s", sr, 23 / 32),
      "3/4-s": plywoodProfileNew(PlywoodProfile["3/4-s"], "3/4-s", sr, 3 / 4),
      "1/2-s": plywoodProfileNew(PlywoodProfile["1/2-s"], "1/2-s", sr, 1 / 2),
      "3/8-s": plywoodProfileNew(PlywoodProfile["3/8-s"], "3/8-s", sr, 3 / 8),
    };
    return substrateCatalog;
  } else if (spt === SubstrateProfileType.OSB) {
    const substrateCatalog: {
      [key in OSBProfile]?: OSB;
    } = {};
    return substrateCatalog;
  }
  return {};
}

export const PlywoodProfileCatalog16 = defaultSubstrateProfileCatalogNew(
  SpanRating["SR16"],
  SubstrateProfileType.PLYWOOD,
);
export const PlywoodProfileCatalog24 = defaultSubstrateProfileCatalogNew(
  SpanRating["SR24"],
  SubstrateProfileType.PLYWOOD,
);
/*To be used only for getting some default*/

export function defaultPlywoodPanelProfileSR16(thickness: number): Plywood {
  if (Math.abs(thickness - 0.5) < ZERO_EPSILON) {
    return PlywoodProfileCatalog16["1/2-u"];
  } else if (Math.abs(thickness - 0.75) < ZERO_EPSILON) {
    return PlywoodProfileCatalog16["3/4-u"];
  } else {
    return PlywoodProfileCatalog16["3/4-u"];
  }
}

export function isSameProfileDescriptor(
  profileDescriptor1: LumberProfileDescriptor,
  profileDescriptor2: LumberProfileDescriptor,
): boolean {
  return (
    profileDescriptor1.type === profileDescriptor2.type &&
    profileDescriptor1.name === profileDescriptor2.name &&
    profileDescriptor1.width === profileDescriptor2.width &&
    profileDescriptor1.height === profileDescriptor2.height &&
    profileDescriptor1.annotationLabel === profileDescriptor2.annotationLabel
  );
}
